import { formatBusinessDate } from './formatBusinessDate';

/**
 * True for a carrier date so far out that it is a "never eligible" marker rather
 * than a real grant date. Verizon writes `12/31/9999`; `DateOnly.MaxValue` is also
 * what Npgsql round-trips as `date 'infinity'` (TD-539). Ten years is well beyond
 * any real contract term - the longest in the verified production file is 3 years.
 *
 * Lives here rather than beside one screen because every surface that renders an
 * eligibility date needs it: rendering the sentinel verbatim tells a user
 * "upgrade eligible Dec 31, 9999".
 */
export function isNeverEligibleSentinel(isoDate: string): boolean {
  const year = Number(isoDate.slice(0, 4));
  return Number.isFinite(year) && year > new Date().getUTCFullYear() + 10;
}

/** The two date fields any eligibility label is derived from. */
export interface LineEligibilityDates {
  upgradeEligibilityDate?: string | null;
  contractEndDate?: string | null;
}

/**
 * Narrow shape of i18next's `t` - the two keys this helper uses both take a
 * `date`. Keeping it to this avoids threading i18next's generics through
 * `src/format/`, which is otherwise framework-free.
 */
export type TranslateWithDate = (key: string, options: { date: string }) => string;

/**
 * The one-line explanation under a line in the request pickers: the carrier's own
 * eligibility date where the feed reports a real one, otherwise the contract end
 * date, otherwise nothing.
 *
 * Preferring the carrier date is BUG-081: eligibility comes from that date where it
 * exists, so labelling a greyed-out row "contract ends Jan 1, 2026" read as a
 * self-contradiction on the 3.7% of real lines whose carrier date falls after
 * contract end, and as an unexplained grant on the 60% where it falls before.
 *
 * A sentinel date is treated as "the carrier reported no date" and falls through to
 * contract end - the same thing `LineDetailPage` does by omitting its
 * "Upgrade eligible from" row while leaving "Contract end" in place. The picker's
 * own disabled note already says "Not yet eligible", so the sentinel would add
 * nothing even if it read sensibly.
 */
export function lineEligibilityLabel(
  line: LineEligibilityDates,
  t: TranslateWithDate,
  language: string,
): string | undefined {
  const carrierDate = line.upgradeEligibilityDate;
  if (carrierDate && !isNeverEligibleSentinel(carrierDate)) {
    return t('form.upgradeEligibleFrom', {
      date: formatBusinessDate(carrierDate, { locale: language }),
    });
  }

  if (line.contractEndDate) {
    return t('form.contractEnds', {
      date: formatBusinessDate(line.contractEndDate, { locale: language }),
    });
  }

  return undefined;
}
